/**
 * Shared player search hook
 *
 * Encapsulates search state, sort state, dual GraphQL query pattern,
 * infinite scroll, results unification, loading derivation, sort handlers,
 * filter chip derivation, and click-outside behavior. Used by every
 * player-search surface — global search, the scouting add-player surface,
 * match-report capture, and the shadow-team / squad-planning add modals.
 * @module components/shared/search-modal/hooks/usePlayerSearch
 */

import { useCallback, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import buildChipRemoveHandlers from "../utils/buildChipRemoveHandlers";

import {
  DEFAULT_SORT_FIELD,
  DEFAULT_SORT_ORDER,
} from "@/features/scouting/constants/sortOptions";
import type { FilterState } from "@/hooks/useFilterState";
import useFilterVariables from "@/features/scouting/hooks/useFilterVariables";
import type { SortOption } from "@/features/scouting/types/scouting-target";
import deriveActiveFilterChips from "@/features/scouting/utils/deriveActiveFilterChips";

import { OrderByDirection } from "@/generated/graphql";
import useClickOutside from "@/hooks/useClickOutside";
import { usePlayerSearchPagination } from "@/hooks/usePlayerSearchPagination";
import { isTransfermarktUrl } from "@/utils/transfermarktUrl";

/** Search debounce delay in ms */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Derives the debounced text-search term for an input. Transfermarkt player
 * URLs are never text-searched — they resolve via the dedicated ID lookup
 * (SE-3599) — so they debounce to an empty term, which skips the query.
 * @param text - Raw search input
 * @returns Normalized text-search term, or empty string for Transfermarkt URLs
 */
const toSearchTerm = (text: string): string =>
  isTransfermarktUrl(text) ? "" : text.toLowerCase().trim();

/** Page size for search results */
const PAGE_SIZE = 20;

/** Options for configuring the usePlayerSearch hook */
interface UsePlayerSearchOptions {
  /** Whether the modal is open */
  readonly isOpen: boolean;
  /** Whether the user has a valid auth token */
  readonly hasAuth: boolean;
  /** Available sort options for the dropdown */
  readonly sortOptions: readonly SortOption[];
  /** Filter state from useFilterState */
  readonly filterState: FilterState;
  /** Optional initial search query — read lazily on mount only */
  readonly initialQuery?: string;
}

/**
 * Shared player search hook encapsulating search, sort, filter, pagination
 * @param options - Configuration options
 * @param options.isOpen - Whether the modal is open
 * @param options.hasAuth - Whether the user has a valid auth token
 * @param options.sortOptions - Available sort options for the dropdown
 * @param options.filterState - Filter state from useFilterState
 * @param options.initialQuery - Optional initial search query (lazy initial state)
 * @returns All search-related state, derived values, and handlers
 */
const usePlayerSearch = ({
  isOpen,
  hasAuth,
  sortOptions,
  filterState,
  initialQuery,
}: UsePlayerSearchOptions) => {
  /* --- Search state — `initialQuery` seeds both pieces on first render only --- */
  const [searchQuery, setSearchQuery] = useState(() => initialQuery ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    toSearchTerm(initialQuery ?? "")
  );
  /*
   * The raw input text as of the last debounce tick — same cadence as
   * `debouncedSearch`, but verbatim rather than lowercased/trimmed. Surfaces
   * that echo what the user typed back at them (e.g. an "Add \"<name>\"
   * manually" affordance) read this instead of `searchQuery`, so the value
   * they derive changes once per debounce rather than once per keystroke
   * (SE-7385).
   */
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(
    () => initialQuery ?? ""
  );

  /** Commits one debounce tick — the raw text and its normalized term together. */
  const commitDebouncedSearch = useCallback((text: string) => {
    setDebouncedSearchQuery(text);
    setDebouncedSearch(toSearchTerm(text));
  }, []);

  const debouncedSetSearch = useDebouncedCallback(
    commitDebouncedSearch,
    SEARCH_DEBOUNCE_MS
  );

  /* --- Sort state (null = no explicit selection, uses DEFAULT_SORT_FIELD for queries) --- */
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] =
    useState<OrderByDirection>(DEFAULT_SORT_ORDER);
  const [isSortOpen, setIsSortOpen] = useState(false);

  /** Effective sort field for queries — falls back to GPR when no explicit selection */
  const effectiveSortBy = sortBy ?? DEFAULT_SORT_FIELD;

  /* --- Determine which query to use based on active filters --- */
  const hasFilters = filterState.activeFilterCount > 0;

  /* --- Filter variables for filterPlayers query --- */
  const filterVariables = useFilterVariables(filterState, debouncedSearch);

  /* --- Search + pagination orchestration via shared hook --- */
  const {
    players: searchPlayers,
    isLoading,
    isRefreshing,
    isEmpty,
    isEmptyDueToAutoLoadBudget,
    isError,
    hasSearched,
    loadMore,
    hasMore,
    isLoadingMore,
  } = usePlayerSearchPagination({
    isOpen,
    hasAuth,
    debouncedSearch,
    sortBy,
    sortOrder,
    filterVariables,
    hasFilters,
    pageSize: PAGE_SIZE,
  });

  /* --- Callbacks --- */
  const handleSearchChange = useCallback(
    (text: string) => {
      setSearchQuery(text);
      debouncedSetSearch(text);
    },
    [debouncedSetSearch]
  );

  const handleClearSearch = useCallback(() => {
    debouncedSetSearch.cancel();
    setSearchQuery("");
    setDebouncedSearch("");
    setDebouncedSearchQuery("");
  }, [debouncedSetSearch]);

  const handleToggleSort = useCallback(() => {
    setIsSortOpen(prev => !prev);
  }, []);

  const handleCloseSort = useCallback(() => {
    setIsSortOpen(false);
  }, []);

  const handleSetAscending = useCallback(() => {
    setSortOrder(OrderByDirection.Asc);
  }, []);

  const handleSetDescending = useCallback(() => {
    setSortOrder(OrderByDirection.Desc);
  }, []);

  /* --- Sort handlers — clicking the active field deselects (sets null) --- */
  const sortHandlers = useMemo(
    () =>
      Object.fromEntries(
        sortOptions.map(option => {
          return [
            option.field,
            () => {
              const deselecting = sortBy === option.field;
              setSortBy(deselecting ? null : option.field);
              setSortOrder(deselecting ? DEFAULT_SORT_ORDER : sortOrder);
              setIsSortOpen(false);
            },
          ];
        })
      ) as Record<string, () => void>,
    [sortOptions, sortBy, sortOrder]
  );

  /* --- Derived values --- */
  const sortFieldLabel =
    sortOptions.find(opt => opt.field === effectiveSortBy)?.label ?? "GPR";

  /* --- Active filter chips below search bar --- */
  const activeFilterChips = useMemo(
    () => deriveActiveFilterChips(filterState),
    [filterState]
  );

  const chipRemoveHandlers = useMemo(
    () => buildChipRemoveHandlers(filterState),
    [filterState]
  );

  /* --- Sort dropdown props --- */
  const sortDropdownProps = useMemo(
    () => ({
      isOpen: isSortOpen,
      sortOptions,
      selectedSortField: sortBy,
      sortDirection: sortOrder,
      sortHandlers,
      onAscending: handleSetAscending,
      onDescending: handleSetDescending,
      onClose: handleCloseSort,
    }),
    [
      isSortOpen,
      sortOptions,
      sortBy,
      sortOrder,
      sortHandlers,
      handleSetAscending,
      handleSetDescending,
      handleCloseSort,
    ]
  );

  /** Resets search text and sort state — called by containers on modal close */
  const resetSearchAndSort = useCallback(() => {
    debouncedSetSearch.cancel();
    setSearchQuery("");
    setDebouncedSearch("");
    setDebouncedSearchQuery("");
    setIsSortOpen(false);
    setSortBy(null);
    setSortOrder(DEFAULT_SORT_ORDER);
  }, [debouncedSetSearch]);

  /**
   * External setter for searchQuery — bypasses debounce so the results query
   * reflects the new value immediately. Used by GlobalSearchModalContainer
   * to hydrate from globalSearchQueryVar on the isOpen rising edge.
   * @param next - Replacement query string
   */
  const replaceSearchQuery = useCallback(
    (next: string) => {
      debouncedSetSearch.cancel();
      setSearchQuery(next);
      setDebouncedSearch(toSearchTerm(next));
      setDebouncedSearchQuery(next);
    },
    [debouncedSetSearch]
  );

  /* --- Close sort dropdown when clicking outside on web --- */
  useClickOutside(isSortOpen, "[data-sort-dropdown]", handleCloseSort);

  return {
    // Search state
    searchQuery,
    debouncedSearch,
    debouncedSearchQuery,
    handleSearchChange,
    handleClearSearch,

    // Sort state
    sortBy,
    effectiveSortBy,
    sortFieldLabel,
    handleToggleSort,
    sortDropdownProps,

    // Results
    searchPlayers,
    hasSearched,
    isLoading,
    isRefreshing,
    isEmpty,
    isEmptyDueToAutoLoadBudget,
    isError,
    hasMore,
    loadMore,
    isLoadingMore,

    // Filter chips
    activeFilterChips,
    chipRemoveHandlers,

    // Reset
    resetSearchAndSort,

    // External setter for hydration (e.g. globalSearchQueryVar on rising edge)
    replaceSearchQuery,
  };
};

export default usePlayerSearch;
